import React from 'react';
import type { AxiosError } from 'axios';
import { type IDisclosureOptions, type ISendPhoneVerificationCodeOptions, Server } from '../../resources/Server';
import { useResendCodeInterval } from './useResendCodeInterval';

export function useSendPhoneVerificationCode(args: IDisclosureOptions) {
  const { envelopeId, signerId } = args;

  const [sendCodeError, setSendCodeError] = React.useState<string | null>(null);
  const [isSendingCode, setIsSendingCode] = React.useState(false);
  const [verificationCode, setVerificationCode] = React.useState<string | null>(null);
  const resendInterval = useResendCodeInterval();

  const sendCode = React.useCallback(
    async (args: Pick<ISendPhoneVerificationCodeOptions, 'deliveryMethod' | 'phoneNumber'>) => {
      const { deliveryMethod, phoneNumber } = args;
      try {
        setIsSendingCode(true);
        const res = await new Server().sendPhoneVerificationCode({ envelopeId, signerId, phoneNumber, deliveryMethod });
        setVerificationCode(res.result.code);
        setSendCodeError(null);
      } catch (e) {
        const err = e as AxiosError;
        const msg = 'Error sending verification code';
        console.error(`${msg}: ${err.message}`, e);
        setSendCodeError(msg);
      } finally {
        setIsSendingCode(false);
        resendInterval.resetTimer();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [envelopeId, signerId]
  );

  const verifyCode = React.useCallback(
    (code: string) => {
      if (verificationCode === code) {
        setSendCodeError(null);
        return true;
      }
      setSendCodeError('Invalid verification code');
      return false;
    },
    [verificationCode]
  );

  return {
    error: sendCodeError,
    isLoading: isSendingCode,
    secsUntilResend: resendInterval.secsUntilResend,
    mutateAsync: sendCode,
    verificationCode,
    verify: verifyCode,
  };
}
